import { useState, useRef } from "react";

export const DEFAULT_ITEMS_TO_DISPLAY = 2;

export interface UseCarouselParams<ItemProps> {
  items: Array<ItemProps>;
  numberOfItemsToDisplay?: number;
  initialFocusedItemIndex?: number;
}

interface UseCarouselResponse {
  context: {
    currentIndex: number;
    containerRef: React.RefObject<HTMLDivElement>;
    previousDisabled: boolean;
    nextDisabled: boolean;
  };
  handlers: {
    onDotClick: (index: number) => void;
    onNextClick: () => void;
    onPreviousClick: () => void;
  };
}

export const useCarousel = <T>({
  items,
  numberOfItemsToDisplay = DEFAULT_ITEMS_TO_DISPLAY,
  initialFocusedItemIndex = 0,
}: UseCarouselParams<T>): UseCarouselResponse => {
  const [currentIndex, setCurrentIndex] = useState<number>(
    initialFocusedItemIndex
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const getContainer = () => containerRef?.current;

  const getItemWidth = () => {
    const container = getContainer();
    return !container ? 0 : container?.clientWidth / numberOfItemsToDisplay;
  };

  const onNextClick = () => {
    const container = getContainer();
    if (!container) {
      return;
    }
    onDotClick((currentIndex + 1) % items.length);
  };

  const onPreviousClick = () => {
    const container = getContainer();
    if (!container) {
      return;
    }
    onDotClick((currentIndex - 1 + items.length) % items.length);
  };

  const onDotClick = (index) => {
    const container = getContainer();
    if (!container) {
      return;
    }

    container.scrollLeft = getItemWidth() * index;
    setCurrentIndex(index);
  };

  return {
    context: {
      currentIndex,
      containerRef,
      previousDisabled: currentIndex === 0,
      nextDisabled: currentIndex === items.length - 1,
    },
    handlers: { onDotClick, onNextClick, onPreviousClick },
  };
};
